<template>
  <div class="bg-white rounded-lg border border-gray-200 p-5">
    <div class="flex items-center justify-between mb-1">
      <div class="text-sm font-semibold text-gray-800">Medicare Claims by Date of Service</div>
      <div v-if="pipeline" class="text-xs text-gray-500 space-x-4">
        <span>
          BCDA through:
          <span class="font-semibold text-gray-800">{{ formatDate(pipeline.bcda?.data_through_date) }}</span>
          <span class="ml-1" :class="freshnessClass(pipeline.bcda?.data_through_date)">
            ({{ freshnessLabel(pipeline.bcda?.data_through_date) }})
          </span>
        </span>
        <span>
          CCLF through:
          <span class="font-semibold text-gray-800">{{ formatDate(pipeline.cclf?.data_through_date) }}</span>
          <span class="ml-1" :class="freshnessClass(pipeline.cclf?.data_through_date)">
            ({{ freshnessLabel(pipeline.cclf?.data_through_date) }})
          </span>
        </span>
      </div>
    </div>
    <div class="text-xs text-gray-400 mb-4">
      Daily Medicare claims volume by date of service. A sharp drop-off indicates the boundary of complete data from CMS.
    </div>

    <div v-if="loading" class="flex items-center justify-center py-16">
      <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-dark"></div>
    </div>
    <div v-else-if="error" class="text-xs text-red-500 py-4">
      Couldn't load claims pipeline.
      <button type="button" class="underline hover:no-underline" @click="fetchData">Retry</button>
    </div>
    <Chart_V5
      v-else-if="combinedChartData.labels.length > 0"
      :chart_data="combinedChartData"
      :chart_config="chartConfig"
      override-height="h-[300px]"
    />
    <div v-else class="text-xs text-gray-400 py-4">No claims in the last 60 days.</div>
  </div>
</template>

<script>
import axios from 'axios';
import Chart_V5 from '@/components/widgetcomponents/Viz_Elements/Chart_V5.vue';

/**
 * Claims pipeline chart, loading independently of the freshness cards and
 * feed registry. Owns its own /api/pipeline-status/ request + loading/error.
 */
export default {
  name: 'ClaimsPipelineChart',
  components: { Chart_V5 },
  props: {
    entityId: { type: Number, default: null },
  },
  data() {
    return {
      loading: false,
      error: false,
      pipeline: null,
    };
  },
  computed: {
    combinedChartData() {
      const bcda = this.pipeline?.bcda?.daily || [];
      const cclf = this.pipeline?.cclf?.daily || [];
      const dateSet = new Set([...bcda.map(d => d.date), ...cclf.map(d => d.date)]);
      const dates = [...dateSet].sort();
      const bcdaMap = Object.fromEntries(bcda.map(d => [d.date, d.count]));
      const cclfMap = Object.fromEntries(cclf.map(d => [d.date, d.count]));
      return {
        labels: dates.map(d => this.shortDate(d)),
        series: [
          {
            values: dates.map(d => bcdaMap[d] || 0),
            config: { label: 'BCDA', backgroundColor: 'rgba(59, 130, 246, 0.7)', type: 'bar', yAxis: 'left', offset: 0 },
          },
          {
            values: dates.map(d => cclfMap[d] || 0),
            config: { label: 'CCLF', backgroundColor: 'rgba(16, 185, 129, 0.7)', type: 'bar', yAxis: 'left', offset: 1 },
          },
        ],
      };
    },
    chartConfig() {
      return {
        chartType: 'vertical_bar',
        margin: { top: 20, right: 20, bottom: 60, left: 80 },
        maxBarWidth: 40,
        xAxis: { labelRotation: -45, fontSize: 11 },
        yAxes: [{ position: 'left', fontSize: 11 }],
        legend: { display: true, position: 'subtitle', spacing: 15 },
        formatting: { percentage: false, sig_figs: 0 },
        dataLabels: { display: true, fontSize: 10, position: 'top' },
      };
    },
  },
  watch: {
    entityId() { this.fetchData(); },
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      this.loading = true;
      this.error = false;
      try {
        const params = {};
        if (this.entityId) params.entity_id = this.entityId;
        const resp = await axios.get('api/pipeline-status/', { params });
        this.pipeline = resp.data;
      } catch (e) {
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
    shortDate(d) {
      if (!d) return '';
      const dt = new Date(d + 'T00:00:00');
      return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    },
    formatDate(d) {
      if (!d) return '—';
      const dt = new Date(d.length <= 10 ? d + 'T00:00:00' : d);
      return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },
    freshnessClass(date) {
      if (!date) return 'text-gray-400';
      const days = Math.floor((new Date() - new Date(date + 'T00:00:00')) / 86400000);
      if (days <= 7) return 'text-green-600';
      if (days <= 21) return 'text-yellow-600';
      return 'text-red-500';
    },
    freshnessLabel(date) {
      if (!date) return 'No data';
      const days = Math.floor((new Date() - new Date(date + 'T00:00:00')) / 86400000);
      if (days === 0) return 'Today';
      if (days === 1) return '1 day ago';
      return `${days} days ago`;
    },
  },
};
</script>
